import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, signInWithGoogle } from '../services/firebase';
import { Terminal, ShieldAlert, Mail } from 'lucide-react';

const Login: React.FC = () => {
    const { user, loading } = useAuth();
    const navigate = useNavigate();

    const [errorMsg, setErrorMsg] = React.useState<string | null>(null);
    const [isLoggingIn, setIsLoggingIn] = React.useState(false);

    const handleLogin = async () => {
        if (isLoggingIn) return;
        try {
            setIsLoggingIn(true);
            setErrorMsg(null);
            await signInWithGoogle();
        } catch (error: any) {
            console.error("Login Error:", error);
            if (error.code === 'auth/cancelled-popup-request' || error.code === 'auth/popup-blocked' || error.code === 'auth/popup-closed-by-user') {
                setErrorMsg("Popup de login bloqueado ou fechado. Tente novamente clicando no botão.");
            } else {
                const msg = error.message || "Erro desconhecido";
                setErrorMsg(msg);
                alert(`Erro: ${msg} (Código: ${error.code})`);
            }
        } finally {
            setIsLoggingIn(false);
        }
    };

    React.useEffect(() => {
        if (user) {
            // Simple logic: if email is from specific domain/user (optional), go to admin
            // For now, everyone goes to GAME, but Admin can navigate to /admin manually or we check permissions later
            // Let's send everyone to /game by default
            navigate('/game');
        }
    }, [user, navigate]);

    if (loading) return <div className="min-h-screen bg-black flex items-center justify-center text-white font-mono">Carregando sistema...</div>;

    return (
        <div className="min-h-screen bg-black flex flex-col items-center justify-center p-4 relative overflow-y-auto overflow-x-hidden pb-32 font-mono">
            {/* Background Image */}
            <div className="fixed inset-0 bg-[url('/bgsite.png')] bg-cover bg-center pointer-events-none"></div>

            {/* Content Container positioned over background */}
            <div className="relative z-10 w-full max-w-md mt-10 md:mt-0 md:ml-auto md:mr-[10%] xl:mr-[15%] flex flex-col gap-6">

                <div className="text-center relative z-20">
                    <img src="/logo2.png" alt="Arquivos ACME" className="w-64 max-w-[80vw] mx-auto drop-shadow-2xl" />
                </div>

                {/* Game Info Box */}
                <div className="bg-black/90 border border-zinc-700 text-zinc-300 p-5 rounded-lg text-sm text-center shadow-2xl backdrop-blur-sm relative overflow-hidden group">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-zinc-500 to-transparent opacity-50"></div>
                    <p className="font-serif text-lg tracking-widest text-white mb-2 uppercase">Agência Detetive ACME</p>
                    <p className="text-xs leading-relaxed text-zinc-400">Assuma sua licença de investigador, analise as evidências, decifre arquivos criptografados e chegue até a verdade. Tudo depende da sua capacidade de dedução.</p>
                </div>

                {/* Vintage ID Card Style Container for Login */}
                <div className="bg-[#e8deb5] border-8 border-double border-[#8b7355] p-8 rounded-sm shadow-[10px_10px_30px_rgba(0,0,0,0.9)] relative">
                    {/* Decorative Corner Elements */}
                    <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-zinc-500 rounded-tl"></div>
                    <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-zinc-500 rounded-tr"></div>
                    <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-zinc-500 rounded-bl"></div>
                    <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-zinc-500 rounded-br"></div>

                    <button
                        onClick={handleLogin}
                        disabled={isLoggingIn}
                        className={`w-full py-4 mt-6 bg-transparent border-4 border-red-800 text-red-800 hover:bg-red-800/10 font-bold rounded-sm flex items-center justify-center gap-3 transition-colors text-2xl tracking-widest uppercase font-serif ${isLoggingIn ? 'opacity-50 cursor-not-allowed' : ''}`}
                        style={{ textShadow: '0 0 1px rgba(153,27,27,0.5)' }}
                    >
                        {isLoggingIn ? 'AUTENTICANDO...' : 'ENTRAR NO SISTEMA'}
                    </button>

                    {errorMsg && (
                        <div className="mt-4 text-center text-xs font-bold text-red-800 bg-red-800/10 p-2 rounded">
                            {errorMsg}
                        </div>
                    )}

                    <div className="mt-6 text-center text-sm text-[#4a3f35] font-serif font-bold tracking-wide">
                        <p className="opacity-50">ACESSO RESTRITO A AGENTES</p>
                    </div>
                </div>

                {/* Privacy and Policy Disclaimer */}
                <div className="bg-black/80 border border-zinc-800 rounded p-4 text-[10px] text-zinc-400 text-center flex flex-col gap-2 shadow-xl backdrop-blur-md">
                    <div className="flex items-center justify-center gap-2 text-zinc-300 mb-1">
                        <ShieldAlert className="w-4 h-4" />
                        <span className="font-bold uppercase tracking-widest">Política de Privacidade & Cookies</span>
                    </div>
                    <p>Ao prosseguir com o login, você concorda com a habilitação de cookies essenciais para a sua sessão e com a nossa Política de Privacidade.</p>
                    <p>Seu nome e e-mail informados ao Google são utilizados <strong>exclusivamente para a criação do seu registro</strong> no jogo.</p>
                    <p>O CPF/CNPJ (quando solicitado em compras) é recolhido e tratado pela <strong className="text-zinc-200">Asaas (Instituição de Pagamento)</strong> para atender as regulamentações obrigatórias de segurança e registros do Banco Central do Brasil. O jogo não armazena seu CPF localmente de forma exposta.</p>
                </div>

            </div>

            {/* Global Footer (Liluntech & Contacts) */}
            <div className="absolute bottom-0 left-0 w-full bg-black/95 border-t border-zinc-900 px-6 py-4 flex flex-col md:flex-row items-center justify-between z-20 gap-4">
                <div className="flex flex-col text-xs text-zinc-500 gap-1 text-center md:text-left">
                    <a href="mailto:contato@acme.liluntech.com.br" className="flex items-center justify-center md:justify-start gap-1 hover:text-indigo-400 transition-colors">
                        <Mail className="w-3 h-3" /> contato@acme.liluntech.com.br
                    </a>
                    <span className="opacity-50 italic">Suporte interno pelo painel em breve.</span>
                </div>

                <div className="flex items-center gap-2 opacity-80 hover:opacity-100 transition-opacity">
                    <span className="text-zinc-600 text-[10px] uppercase font-bold tracking-widest mr-1">Desenvolvido por</span>
                    <a href="https://liluntech.com.br" target="_blank" rel="noreferrer" className="flex items-center gap-2 group">
                        <Terminal className="w-6 h-6 text-indigo-400 group-hover:scale-110 transition-transform" />
                        <span className="text-xl font-sans font-bold tracking-tight text-white">LilunTech</span>
                    </a>
                </div>
            </div>
        </div>
    );
};

export default Login;
